/**
 * Clampeo de elementos flotantes de las guías AR al viewport visible.
 *
 * El bubble del label (`FloatingLabel`) se dibuja en el SVG del overlay con
 * coordenadas documento-absolutas y ancho fijo. Sin clampeo, un target pegado
 * al borde izquierdo (ítems del menú lateral: x ≈ 8-24px) dejaba ~45% del
 * globo fuera de la pantalla, con el texto cortado a la mitad.
 */

export interface ViewportBounds {
  /** Scroll horizontal del documento (window.scrollX). */
  scrollX: number;
  /** Scroll vertical del documento (window.scrollY). */
  scrollY: number;
  /** Ancho visible del viewport (window.innerWidth). */
  width: number;
  /** Alto visible del viewport (window.innerHeight). */
  height: number;
}

/** Viewport actual (aislado para que clampToViewport sea puro y testeable). */
export const currentViewport = (): ViewportBounds => ({
  scrollX: window.scrollX,
  scrollY: window.scrollY,
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * Devuelve la posición (x, y) de un rectángulo de `width`×`height` empujado
 * dentro del viewport si se iría por algún borde. Sin cambios si ya entra.
 */
export function clampToViewport(
  x: number,
  y: number,
  width: number,
  height: number,
  viewport: ViewportBounds,
  margin = 12,
): { x: number; y: number } {
  const minX = viewport.scrollX + margin;
  const minY = viewport.scrollY + margin;
  const maxX = viewport.scrollX + viewport.width - width - margin;
  const maxY = viewport.scrollY + viewport.height - height - margin;
  return {
    x: Math.max(minX, Math.min(x, maxX)),
    y: Math.max(minY, Math.min(y, maxY)),
  };
}

/**
 * X de la flechita del bubble: sigue al target pero se mantiene dentro del
 * ancho del globo clampeado (para que la cola nunca apunte afuera del globo).
 */
export const clampTailX = (targetX: number, boxX: number, boxWidth: number): number =>
  Math.min(Math.max(targetX, boxX + 16), boxX + boxWidth - 16);
